"use client";

import { useState, useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useMediaStore } from '@/shared/store/voice-chat';
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent';
import { useVadStore } from '@/shared/store/vad';
import type {
  UseConsentControllerOptions,
  UseConsentControllerReturn
} from '../types/consent';
import { CONSENT_BEHAVIOR } from '../const/consent-config';

/**
 * 동의 관리 컨트롤러 훅
 *
 * 책임:
 * - 마이크 사용 동의 상태 관리
 * - 동의 모달 표시/숨김 제어
 * - 동의 완료/거부 시 적절한 상태 업데이트
 * - VAD 상태 제어 (동의 전 비활성화)
 */
export const useConsentController = (
  options: UseConsentControllerOptions = {}
): UseConsentControllerReturn => {
  const {
    onConsentGranted,
    onConsentDeclined,
    enableLogging = CONSENT_BEHAVIOR.ENABLE_LOGGING,
    initialVisible = CONSENT_BEHAVIOR.SHOW_ON_MOUNT,
  } = options;

  // 로컬 상태 - 모달 표시 여부 (초기값: SHOW_ON_MOUNT 설정에 따름)
  const [isConsentModalVisible, setIsConsentModalVisible] = useState(initialVisible);

  // 스토어 액세스
  const hasConsented = useRealtimeAgentStore((state) => state.hasConsented);
  const setHasConsented = useRealtimeAgentStore((state) => state.setHasConsented);
  const setIsMuted = useMediaStore((state) => state.setIsMuted);
  const isMuted = useMediaStore((state) => state.isMuted);
  const setVadStatus = useVadStore((state) => state.setVadStatus);

  // Realtime Agent Store에서 초기화 상태 구독
  const { isAvatarLoading, isGreetingLoading } = useRealtimeAgentStore(
    useShallow(state => ({
      isAvatarLoading: state.isAvatarLoading,
      isGreetingLoading: state.isGreetingLoading,
    }))
  );

  // 전체 초기화가 진행 중인지 확인
  const isInitializing = isAvatarLoading || isGreetingLoading;

  /**
   * VAD 상태 제어 - 동의가 완료되지 않았고 모달이 보이면 VAD 비활성화
   */
  useEffect(() => {
    if (isConsentModalVisible && !hasConsented) {
      setVadStatus('paused');
      if (enableLogging) {
        console.log('🔒 [CONSENT] 동의 모달 표시 - VAD 비활성화');
      }
    }
  }, [isConsentModalVisible, hasConsented, setVadStatus, enableLogging]);

  /**
   * 동의 모달 표시
   */
  const showConsentModal = useCallback(() => {
    if (hasConsented) {
      if (enableLogging) {
        console.log('✅ [CONSENT] 이미 동의됨 - 모달 생략');
      }
      return;
    }

    setIsConsentModalVisible(true);
    setVadStatus('paused');
    if (enableLogging) {
      console.log('📋 [CONSENT] 동의 모달 표시');
    }
  }, [hasConsented, enableLogging, setVadStatus]);

  /**
   * 동의 모달 숨김
   */
  const hideConsentModal = useCallback(() => {
    setIsConsentModalVisible(false);
    if (enableLogging) {
      console.log('📋 [CONSENT] 동의 모달 숨김');
    }
  }, [enableLogging]);

  /**
   * 동의 승인
   */
  const grantConsent = useCallback(() => {
    if (enableLogging) {
      console.log('🔓 [CONSENT] grantConsent 호출됨');
    }

    // 상태 업데이트
    setHasConsented(true);

    if (enableLogging) {
      console.log('🔓 [CONSENT] 동의 상태 설정 완료 - hasConsented: true');
    }

    // 마이크 활성화
    if (CONSENT_BEHAVIOR.AUTO_START_VOICE_ON_CONSENT && isMuted) {
      setIsMuted(false);
      if (enableLogging) {
        console.log('🎤 [CONSENT] 동의 완료 - 마이크 활성화');
      }
    }

    if (enableLogging) {
      console.log('✅ [CONSENT] 동의 완료 - 초기화 대기 중');
    }

    // 외부 콜백 호출
    onConsentGranted?.();

    // Modal은 isInitializing이 false가 될 때 자동으로 닫힘
  }, [setHasConsented, setIsMuted, isMuted, onConsentGranted, enableLogging]);

  /**
   * 동의 거부
   */
  const declineConsent = useCallback(() => {
    // 모달 숨김
    setIsConsentModalVisible(false);

    // 마이크 비활성화 상태 유지
    if (CONSENT_BEHAVIOR.SWITCH_TO_CHAT_ON_DECLINE) {
      setIsMuted(true);
    }

    if (enableLogging) {
      console.log('❌ [CONSENT] 동의 거부됨 - 채팅 모드로 전환');
    }

    // 외부 콜백 호출
    onConsentDeclined?.();
  }, [setIsMuted, onConsentDeclined, enableLogging]);

  /**
   * 동의 상태 초기화 (뒤로 가기 등에서 사용)
   */
  const resetConsent = useCallback(() => {
    // 1. 로컬 상태 초기화
    setHasConsented(false);

    // 2. VAD 비활성화
    setVadStatus('paused');

    // 3. 모달 표시 (마지막에 설정)
    setIsConsentModalVisible(initialVisible);

    if (enableLogging) {
      console.log('🔄 [CONSENT] 동의 상태 초기화');
    }
  }, [initialVisible, setHasConsented, setVadStatus, enableLogging]);

  /**
   * 동의 필요 여부 확인 및 요청
   * @returns true if consent is needed (modal will be shown)
   */
  const checkAndRequestConsent = useCallback((): boolean => {
    if (hasConsented) {
      return false;
    }

    showConsentModal();
    return true;
  }, [hasConsented, showConsentModal]);

  /**
   * 동의 상태에 따라 모달 표시/숨김 자동 관리
   */
  useEffect(() => {
    if (hasConsented) {
      // 동의 완료 후 초기화가 끝나면 모달 숨김
      if (!isInitializing) {
        if (enableLogging) {
          console.log('🎉 [CONSENT] 초기화 완료 - 모달 숨김');
        }
        setIsConsentModalVisible(false);
      }
    }
  }, [hasConsented, isInitializing, enableLogging]);

  return {
    // 상태
    hasConsented,
    isConsentModalVisible,
    isInitializing,
    isAvatarLoading,
    isGreetingLoading,

    // 액션
    showConsentModal,
    hideConsentModal,
    grantConsent,
    declineConsent,
    resetConsent,

    // 헬퍼
    checkAndRequestConsent,
  };
};
